import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const SLIDES = [
  {
    src: "/images/2025-09-07_09-07-51_267.png",
    after: "/images/300s.jpg",
    title: "Before · After · Infinity QX60",
    caption: "Twilight detail. Paint depth restored, glass clarified, trim refreshed.",
  },
  {
    src: "/images/300s-25.jpg",
    title: "Exterior · BMW 5-Series",
    caption: "Foam wash, paint-safe contact.",
  },
  {
    src: "/images/2025-09-07_09-07-55_965.png",
    after: "/images/300s-3.jpg",
    title: "Interior · Rear Cabin",
    caption: "Leather conditioned, vacuumed.",
  },
  {
    src: "/images/300s-9.jpg",
    title: "Exterior · Front",
    caption: "Showroom Finish.",
  },
];

export default function Carousel() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState(() => SLIDES.map(() => false));

  const markLoaded = (i) => setLoaded((prev) => {
    if (prev[i]) return prev;
    const next = [...prev];
    next[i] = true;
    return next;
  });

  const scrollTo = (i) => {
    const clamped = Math.max(0, Math.min(i, SLIDES.length - 1));
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[clamped];
    if (slide) {
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: "smooth" });
      setActive(clamped);
    }
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children);
      const left = track.scrollLeft;
      let best = 0, bestDist = Infinity;
      slides.forEach((s, idx) => {
        const d = Math.abs(s.offsetLeft - track.offsetLeft - left);
        if (d < bestDist) { bestDist = d; best = idx; }
      });
      setActive(best);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="work"
      className="relative section"
      data-testid="carousel-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="overline mb-6 text-gold-bright">Recent work</p>
          <h2 className="section-heading">
            Cars from your <br className="hidden sm:block" /> neighborhood.
          </h2>
          <p className="mt-6 text-lg leading-relaxed max-w-md" style={{ letterSpacing: "-0.01em", color: "#b8b8c0" }}>
            Every car below was detailed in a Niskayuna driveway, by us. Swipe to see more.
          </p>
        </motion.div>
        <div className="hidden md:flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollTo(active - 1)}
            disabled={active === 0}
            className="w-12 h-12 rounded-full border border-white/15 hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-foreground transition-colors"
            style={{ backdropFilter: "blur(12px)" }}
            data-testid="carousel-prev"
            aria-label="Previous"
          >
            <ArrowLeft size={16} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollTo(active + 1)}
            disabled={active === SLIDES.length - 1}
            className="w-12 h-12 rounded-full border border-white/15 hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-foreground transition-colors"
            style={{ backdropFilter: "blur(12px)" }}
            data-testid="carousel-next"
            aria-label="Next"
          >
            <ArrowRight size={16} />
          </motion.button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-5 md:gap-7 overflow-x-auto snap-x snap-mandatory px-5 sm:px-8 pb-10"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        data-testid="carousel-track"
      >
        {SLIDES.map((s, i) => (
          <motion.figure
            key={s.src}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.9,
              delay: i * 0.07,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="snap-center shrink-0 w-[88vw] sm:w-[60vw] md:w-[520px] aspect-[4/5] rounded-3xl overflow-hidden relative group cursor-pointer"
            style={{
              background: "#131313",
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
            whileHover={{ y: -6, scale: 1.01 }}
            data-testid={`carousel-slide-${i}`}
          >
            {!loaded[i] && (
              <Skeleton className="absolute inset-0 w-full h-full" style={{ borderRadius: 0 }} />
            )}
            <img
              src={s.src}
              alt={s.title}
              className="absolute inset-0 w-full h-full object-cover carousel-before"
              style={{
                filter: "brightness(1.05) contrast(1.06) saturate(1.08)",
                opacity: loaded[i] ? 1 : 0,
                transition: "opacity 0.4s ease",
              }}
              loading="lazy"
              onLoad={() => markLoaded(i)}
            />
            {s.after && (
              <img
                src={s.after}
                alt={`${s.title} — after`}
                className="absolute inset-0 w-full h-full object-cover carousel-after"
                style={{ filter: "brightness(1.08) contrast(1.08) saturate(1.1)" }}
                loading="lazy"
                onLoad={() => markLoaded(i)}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
              style={{
                background:
                  "linear-gradient(115deg, transparent 35%, rgba(170,170,170,0.18) 50%, transparent 65%)",
              }}
            />
            <figcaption className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
              <p className="text-[10px] tracking-[0.28em] uppercase text-white/70 mb-2 font-mono">
                {s.title}
              </p>
              <p
                className="text-white text-base md:text-lg font-medium"
                style={{ letterSpacing: "-0.01em" }}
              >
                {s.caption}
              </p>
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <div className="flex justify-center gap-2 mt-4">
        {SLIDES.map((_, i) => (
          <motion.button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`carousel-dot-${i}`}
            className="h-1.5 rounded-full"
            animate={{
              width: active === i ? 32 : 6,
              backgroundColor: active === i ? "#8A8A8A" : "rgba(245,245,247,0.25)",
            }}
            whileHover={{ backgroundColor: active === i ? "#AAAAAA" : "rgba(245,245,247,0.5)" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
      </div>
    </section>
  );
}
